import { handleShareClick } from '/js/shareLink.js';
import { initComuneAutocomplete, getComuneSelezionato } from '/js/addizionaliComunali.js';
import { calcolaPivaOrdinaria } from '/js/pivaOrdinariaCalc.js';
import { confrontaRegimi, coefficienteDefault } from '/js/confrontoRegimi.js';
import { renderConfronto, linkAlForfettario } from '/js/confrontoRegimiUI.js';
import { initCostiBuilder, aggiornaContestoCosti, getCostiDettaglio, applicaDettaglio } from '/js/costiDeducibiliUI.js';
import { calcolaCostiDeducibili, tipoContribuente } from '/js/costiDeducibiliCalc.js';
import { trackConfrontoVisto } from '/js/costiDeducibiliAnalytics.js';
import previdenzaData from '/data/forfettario.json';
import fiscalData from '/data/data.json';
import costiCatalogo from '/data/costi-deducibili.json';

const isResultsPage = window.location.pathname === '/piva-ordinaria/risultati';
const SHARE_CAMPAIGN = 'sharing_link';
const BASE_PATH = '/piva-ordinaria';
const RESULTS_PATH = '/piva-ordinaria/risultati';

const encodeParams = (obj) => {
    try {
        return btoa(encodeURIComponent(JSON.stringify(obj)));
    } catch (e) {
        console.error('encodeParams failed', e);
        return null;
    }
};

const decodeParams = (encoded) => {
    try {
        return JSON.parse(decodeURIComponent(atob(encoded)));
    } catch (e) {
        console.error('decodeParams failed', e);
        return null;
    }
};

const formatEuro = (value) =>
    new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        useGrouping: 'always'
    }).format(value);

const formatPct = (value) =>
    `${value.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const gestioni = previdenzaData.gestioni_previdenziali || [];
const findGestione = (id) => gestioni.find((g) => (g.id || g.value) === id) || null;

const cassaByForma = {
    libero_senza_cassa: ['INPS_GS'],
    libero_con_cassa: [
        'CASSA_FORENSE', 'INARCASSA', 'ENPAM', 'ENPACL', 'CNPADC', 'CNPR',
        'CIPAG', 'ENPAP', 'ENPAV', 'ENPAB', 'EPAP', 'EPPI', 'ENPAPI'
    ],
    ditta_artigiana: ['INPS_ART'],
    ditta_commerciale: ['INPS_COM', 'ENASARCO_MONO', 'ENASARCO_PLURI']
};

const isArtCom = (cassaId) => ['INPS_ART', 'INPS_COM'].includes(cassaId);
// L'agente plurimandatario ha massimale e minimale ENASARCO per ogni mandante.
const hasMandantiMultipli = (cassaId) => Boolean(findGestione(cassaId)?.mandanti_multipli);
// La detrazione art. 13 c. 5 TUIR riguarda le imprese: solo per loro ha senso
// chiedere se tengono la contabilita' ordinaria per opzione.
const isImpresa = (forma) => ['ditta_artigiana', 'ditta_commerciale'].includes(forma);

// Ricalcola il dettaglio del costruttore di costi da un payload (?d=) con la
// chiave compatta cd = [[categoriaId, importo], ...]. Il chiamante verifica
// l'invariante di serializzazione (totale dedotto === params.costi) prima di
// fidarsi del dettaglio: se non regge, fa fede il totale.
const ricalcolaDettaglioCosti = (params) => {
    if (!Array.isArray(params?.cd) || params.cd.length === 0) return null;
    const tipo = tipoContribuente(params.formaAttivita, params.cassa, costiCatalogo);
    const voci = params.cd
        .filter((coppia) => Array.isArray(coppia))
        .map(([categoriaId, importo]) => ({ categoriaId, importo }));
    return calcolaCostiDeducibili({ voci, ricavi: params.ricavi, tipo }, costiCatalogo);
};

document.addEventListener('DOMContentLoaded', () => {
    // --- Riferimenti comuni -------------------------------------------------
    const inputSection = document.querySelector('.input-section');
    const resultSection = document.querySelector('.result-section');
    const shareButton = document.getElementById('shareButton');
    const backButton = document.getElementById('backButton');
    const backButtonTop = document.getElementById('pivaBackButtonTop');
    const detailScrollButton = document.getElementById('detailScrollButton');
    const faqScrollButton = document.getElementById('faqScrollButton');
    const accontiScrollButton = document.getElementById('accontiScrollButton');

    // --- Form elements ------------------------------------------------------
    const ricaviElement = document.getElementById('ricavi');
    const ricaviFormattedElement = document.getElementById('ricavi-formatted');
    const costiElement = document.getElementById('costi');
    const formaAttivitaElement = document.getElementById('forma-attivita');
    const cassaElement = document.getElementById('cassa-previdenziale');
    const regioneElement = document.getElementById('regione');
    const dipendenteFullTimeElement = document.getElementById('dipendente-fulltime');
    const riduzioneDipendenteElement = document.getElementById('riduzione-contributi-dipendente');
    const dipendenteRiduzioniGroup = document.getElementById('dipendenteRiduzioniGroup');
    const ralDipendenteGroup = document.getElementById('ralDipendenteGroup');
    const ralDipendenteElement = document.getElementById('ral-dipendente');
    const inpsArtComFields = document.getElementById('inpsArtComFields');
    const mesiAttivitaElement = document.getElementById('mesi-attivita');
    const mesiAttivitaGroup = document.getElementById('mesiAttivitaGroup');
    const nuovaAttivitaArtComElement = document.getElementById('nuova-attivita-artcom');
    const riduzione50Element = document.getElementById('riduzione-50');
    const nuovaAttivitaArtcomGroup = document.getElementById('nuovaAttivitaArtcomGroup');
    const riduzione50Group = document.getElementById('riduzione50Group');
    const ante1996Element = document.getElementById('ante-1996');
    const ante1996Group = document.getElementById('ante1996Group');
    const numeroMandantiElement = document.getElementById('numero-mandanti');
    const numeroMandantiGroup = document.getElementById('numeroMandantiGroup');
    const fasciaEtaEnpamElement = document.getElementById('fascia-eta-enpam');
    const fasciaEtaEnpamGroup = document.getElementById('fasciaEtaEnpamGroup');
    const contabilitaOrdinariaElement = document.getElementById('contabilita-ordinaria');
    const contabilitaOrdinariaGroup = document.getElementById('contabilitaOrdinariaGroup');
    const toggleButton = document.getElementById('toggleButton');

    const requiredFields = ['ricavi', 'costi', 'forma-attivita', 'cassa-previdenziale', 'regione', 'mesi-attivita'];

    // --- Validazione --------------------------------------------------------
    const validateField = (field) => {
        if (!field) return false;
        if (field.id === 'ricavi') return parseFloat(field.value) > 0;
        if (field.id === 'costi') return Number.isFinite(parseFloat(field.value)) && parseFloat(field.value) >= 0;
        if (field.id === 'mesi-attivita') {
            if (!isArtCom(cassaElement?.value)) return true;
            // Con l'esonero il campo e' nascosto: non puo' bloccare il calcolo.
            if (riduzioneDipendenteElement?.checked) return true;
            const m = parseInt(field.value, 10);
            return Number.isInteger(m) && m >= 1 && m <= 12;
        }
        return field.value.trim() !== '';
    };

    const updateFieldHighlighting = () => {
        requiredFields.forEach((id) => document.getElementById(id)?.classList.remove('invalid-field'));
        const firstEmpty = requiredFields.find((id) => {
            const el = document.getElementById(id);
            return el && !validateField(el);
        });
        if (firstEmpty) document.getElementById(firstEmpty)?.classList.add('invalid-field');
    };

    const updateArtComVisibility = () => {
        const artcom = isArtCom(cassaElement?.value);
        inpsArtComFields?.classList.toggle('hidden-block', !artcom);
        // Riduzione 50%, nuova iscrizione e mesi di attivita' hanno senso solo
        // senza l'esonero: se non versi nulla alla gestione non c'e' niente da
        // ridurre, ne' un minimale da rapportare ai mesi. Stessa scelta del
        // calcolatore forfettario (js/forfettario.js).
        const hasRiduzioneDip = Boolean(riduzioneDipendenteElement?.checked);
        if (mesiAttivitaElement) {
            mesiAttivitaElement.required = artcom && !hasRiduzioneDip;
            if (artcom && !mesiAttivitaElement.value) mesiAttivitaElement.value = '12';
        }
        mesiAttivitaGroup?.classList.toggle('hidden-block', hasRiduzioneDip);
        nuovaAttivitaArtcomGroup?.classList.toggle('hidden-block', hasRiduzioneDip);
        riduzione50Group?.classList.toggle('hidden-block', hasRiduzioneDip);
        if (hasRiduzioneDip) {
            if (nuovaAttivitaArtComElement) nuovaAttivitaArtComElement.checked = false;
            if (riduzione50Element) riduzione50Element.checked = false;
        }
        // Il massimale ridotto per anzianita' ante-1996 esiste solo per art/com.
        ante1996Group?.classList.toggle('hidden-block', !artcom);
        if (!artcom && ante1996Element) ante1996Element.checked = false;
    };

    const updateMandantiVisibility = () => {
        const show = hasMandantiMultipli(cassaElement?.value);
        numeroMandantiGroup?.classList.toggle('hidden-block', !show);
        if (!show && numeroMandantiElement) numeroMandantiElement.value = '1';
    };

    // L'ENPAM non ha un minimo unico: la Quota A dipende dalla fascia d'eta'.
    const updateFasciaEtaVisibility = () => {
        const show = cassaElement?.value === 'ENPAM';
        fasciaEtaEnpamGroup?.classList.toggle('hidden-block', !show);
    };

    const updateContabilitaVisibility = () => {
        const show = isImpresa(formaAttivitaElement?.value);
        contabilitaOrdinariaGroup?.classList.toggle('hidden-block', !show);
        if (!show && contabilitaOrdinariaElement) contabilitaOrdinariaElement.checked = false;
    };

    const updateDipendenteVisibility = () => {
        const isDip = Boolean(dipendenteFullTimeElement?.checked);
        dipendenteRiduzioniGroup?.classList.toggle('hidden-block', !isDip);
        ralDipendenteGroup?.classList.toggle('hidden-block', !isDip);
        if (!isDip && riduzioneDipendenteElement) riduzioneDipendenteElement.checked = false;
        if (!isDip && ralDipendenteElement) ralDipendenteElement.value = '';
    };

    const updateCassaOptions = () => {
        if (!cassaElement) return;
        const forma = formaAttivitaElement?.value || '';
        cassaElement.disabled = !forma;
        if (!forma) {
            cassaElement.value = '';
            return;
        }
        const allowed = forma === 'altro' ? null : (cassaByForma[forma] || null);
        Array.from(cassaElement.options || []).forEach((opt) => {
            if (!opt.value) return;
            const ok = !allowed || allowed.includes(opt.value);
            opt.hidden = !ok;
            opt.disabled = !ok;
        });
        if (allowed && allowed.length === 1) {
            cassaElement.value = allowed[0];
        } else if (allowed && !allowed.includes(cassaElement.value)) {
            cassaElement.value = '';
        }
    };

    const updateToggleState = () => {
        const baseOk = ['ricavi', 'costi', 'forma-attivita', 'cassa-previdenziale', 'regione']
            .every((id) => validateField(document.getElementById(id)));
        const mesiOk = !isArtCom(cassaElement?.value) || validateField(mesiAttivitaElement);
        if (toggleButton) toggleButton.disabled = !(baseOk && mesiOk);
        updateFieldHighlighting();
    };

    // --- Ricavi: mirror formattato ------------------------------------------
    const setupRicaviMirror = () => {
        if (!ricaviElement || !ricaviFormattedElement) return;
        ricaviElement.addEventListener('blur', () => {
            let v = parseFloat(ricaviElement.value) || 0;
            if (v < 0) { v = 0; ricaviElement.value = 0; }
            if (v > 0) {
                ricaviFormattedElement.value = v.toLocaleString('it-IT') + '€';
                ricaviElement.style.display = 'none';
                ricaviFormattedElement.style.display = 'block';
            }
        });
        ricaviFormattedElement.addEventListener('focus', () => {
            ricaviFormattedElement.style.display = 'none';
            ricaviElement.style.display = 'block';
            ricaviElement.focus();
        });
    };

    // --- Raccolta / applicazione parametri ----------------------------------
    const collectParams = () => {
        const comune = getComuneSelezionato();
        const fallbackAliquota = parseFloat(document.getElementById('addizionale-comunale')?.value);
        const comunePayload = comune
            ? { nome: comune.nome, esenzione: comune.esenzione, scaglioni: comune.scaglioni, aliquota: comune.aliquota }
            : { aliquota: Number.isFinite(fallbackAliquota) ? fallbackAliquota : 0.8 };
        const params = {
            ricavi: parseFloat(ricaviElement?.value) || 0,
            costi: parseFloat(costiElement?.value) || 0,
            formaAttivita: formaAttivitaElement?.value || '',
            cassa: cassaElement?.value || '',
            regione: regioneElement?.value || '',
            comune: comunePayload,
            dipendenteFullTime: Boolean(dipendenteFullTimeElement?.checked),
            riduzioneDipendente: Boolean(riduzioneDipendenteElement?.checked),
            ralDipendente: parseFloat(ralDipendenteElement?.value) || 0,
            mesiAttivita: parseInt(mesiAttivitaElement?.value, 10) || 12,
            nuovaAttivitaArtCom: Boolean(nuovaAttivitaArtComElement?.checked),
            riduzione50: Boolean(riduzione50Element?.checked),
            ante1996: Boolean(ante1996Element?.checked),
            numeroMandanti: parseInt(numeroMandantiElement?.value, 10) || 1,
            fasciaEtaEnpam: fasciaEtaEnpamElement?.value || 'over40',
            contabilitaOrdinaria: Boolean(contabilitaOrdinariaElement?.checked)
        };
        // Dettaglio del costruttore di costi, solo se ancora allineato a #costi.
        const cd = getCostiDettaglio();
        if (cd) params.cd = cd;
        return params;
    };

    const getParamsFromUrl = () => {
        const search = new URLSearchParams(window.location.search);
        if (search.has('d')) {
            const decoded = decodeParams(search.get('d'));
            if (decoded && typeof decoded === 'object') return decoded;
        }
        return null;
    };

    const applyParamsToForm = (params) => {
        if (!params) return;
        if (ricaviElement) ricaviElement.value = params.ricavi || '';
        if (costiElement) costiElement.value = (params.costi ?? 0);
        if (formaAttivitaElement) formaAttivitaElement.value = params.formaAttivita || '';
        updateCassaOptions();
        if (cassaElement) cassaElement.value = params.cassa || '';
        if (regioneElement) regioneElement.value = params.regione || '';
        if (dipendenteFullTimeElement) dipendenteFullTimeElement.checked = Boolean(params.dipendenteFullTime);
        if (riduzioneDipendenteElement) riduzioneDipendenteElement.checked = Boolean(params.riduzioneDipendente);
        if (ralDipendenteElement) ralDipendenteElement.value = params.ralDipendente || '';
        if (mesiAttivitaElement) mesiAttivitaElement.value = params.mesiAttivita || 12;
        if (nuovaAttivitaArtComElement) nuovaAttivitaArtComElement.checked = Boolean(params.nuovaAttivitaArtCom);
        if (riduzione50Element) riduzione50Element.checked = Boolean(params.riduzione50);
        if (ante1996Element) ante1996Element.checked = Boolean(params.ante1996);
        if (numeroMandantiElement) numeroMandantiElement.value = params.numeroMandanti || 1;
        if (fasciaEtaEnpamElement) fasciaEtaEnpamElement.value = params.fasciaEtaEnpam || 'over40';
        if (contabilitaOrdinariaElement) contabilitaOrdinariaElement.checked = Boolean(params.contabilitaOrdinaria);
        // Ripristino comune: il campo va valorizzato PRIMA di initComuneAutocomplete,
        // cosi' il modulo (che al load riconosce la regione gia' selezionata) ricompone
        // la selezione ufficiale. L'aliquota flat resta come fallback.
        const comuneInput = document.getElementById('comune');
        const aliquotaInput = document.getElementById('addizionale-comunale');
        if (params.comune) {
            if (comuneInput && params.comune.nome) comuneInput.value = params.comune.nome;
            if (aliquotaInput && Number.isFinite(params.comune.aliquota)) {
                aliquotaInput.value = params.comune.aliquota;
            }
        }
    };

    const redirectToResults = () => {
        const encoded = encodeParams(collectParams());
        const qs = encoded ? `d=${encodeURIComponent(encoded)}` : '';
        window.history.replaceState({}, '', qs ? `${BASE_PATH}?${qs}` : BASE_PATH);
        window.location.href = qs ? `${RESULTS_PATH}?${qs}` : RESULTS_PATH;
    };

    const redirectToHomeWithQuery = () => {
        const qs = window.location.search;
        window.location.href = qs ? `${BASE_PATH}${qs}` : BASE_PATH;
    };

    // --- Rendering risultati ------------------------------------------------
    const buildCalcInput = (params) => ({
        ricavi: params.ricavi,
        costiDeducibili: params.costi,
        gestione: findGestione(params.cassa),
        regione: params.regione,
        comune: params.comune || {},
        dipendenteFullTime: params.dipendenteFullTime,
        riduzioneDipendente: params.riduzioneDipendente,
        ralDipendente: params.ralDipendente,
        mesiAttivita: params.mesiAttivita,
        nuovaAttivitaArtCom: params.nuovaAttivitaArtCom,
        riduzione50: params.riduzione50,
        ante1996: params.ante1996,
        numeroMandanti: params.numeroMandanti,
        fasciaEtaEnpam: params.fasciaEtaEnpam,
        contabilitaOrdinaria: params.contabilitaOrdinaria,
        year: '2026'
    });

    const setText = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.textContent = value;
    };
    const setVal = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.value = value;
    };
    const setHtml = (id, value) => {
        const el = document.getElementById(id);
        if (el) el.innerHTML = value;
    };

    const renderAcconti = (r, gestioneId) => {
        const a = r.acconti;
        const I = r.irpefNetta;
        const addizTot = r.addizionaleRegionale + r.addizionaleComunale;
        // Per le scadenze conta quanto esce di tasca: l'integrativo in rivalsa
        // lo incassi dal cliente prima di girarlo alla cassa.
        const C = r.contributiACarico;
        const Cfissi = r.contributiFissi;
        const euro = (v) => `${formatEuro(v)} €`;

        const accIrpefTot = a.irpef.accontoTotale;
        const accInpsTot = a.inps.accontoTotale;
        const hasAcconto = (accIrpefTot + accInpsTot) > 0;

        const annuale = I + addizTot + C;              // imposte + contributi di un anno
        const saldoPrimoAnno = annuale;                // saldo del 1° anno, versato il 2°
        // Seconde rate di novembre (acconti, uguali ogni anno).
        const novRate = a.irpef.acconto2 + a.inps.rata2;
        // 2° anno: saldo del 1° anno + tutti gli acconti (raddoppio).
        const tot2 = saldoPrimoAnno + accIrpefTot + accInpsTot + a.comunale;
        const giu2 = tot2 - novRate;
        // A regime: stesse due scadenze, ma il totale è una sola annualità.
        const giuR = annuale - novRate;

        const row = (date, label, value) =>
            `<div class="piva-scadenza-row"><span class="piva-scadenza-date">${date}</span>` +
            `<span class="piva-scadenza-label">${label}</span>` +
            `<span class="piva-scadenza-value">${euro(value)}</span></div>`;
        const totale = (label, value, tag) =>
            `<div class="piva-anno-totale"><span>${label}</span>` +
            `<span>${euro(value)}${tag ? ` <em>${tag}</em>` : ''}</span></div>`;
        const note = (txt) => `<p class="piva-anno-note">${txt}</p>`;

        // 1° anno: dipende dalla presenza di contributi minimi in rate fisse.
        if (Cfissi > 0) {
            setHtml('annoUno',
                note("Nel primo anno versi solo i contributi minimi INPS, in 4 rate fisse. Saldo e acconto (IRPEF e contributi sull'eccedenza) scattano dal secondo anno.") +
                totale('Contributi minimi nell\'anno', Cfissi));
        } else {
            setHtml('annoUno',
                note("Nel primo anno saldo e acconto non sono dovuti: non c'è un'imposta dell'anno precedente su cui calcolarli. Eventuali contributi minimi di cassa restano dovuti secondo le regole della tua cassa.") +
                totale('Totale versato nell\'anno', 0));
        }

        // 2° anno e a regime.
        if (!hasAcconto) {
            setHtml('annoDue',
                row('30 giu', 'Saldo del 1° anno (imposte + contributi)', saldoPrimoAnno) +
                note('Nessun acconto dovuto: si versa solo il saldo, senza raddoppio.') +
                totale('Totale nell\'anno', saldoPrimoAnno));
            setHtml('annoRegime',
                row('30 giu', 'Saldo dell\'anno (imposte + contributi)', annuale) +
                totale('Totale nell\'anno', annuale));
        } else {
            const ratio = annuale > 0 ? tot2 / annuale : 0;
            const tag2 = ratio >= 1.7 ? '≈ doppio' : 'più pesante';
            setHtml('annoDue',
                row('30 giu', 'Saldo del 1° anno + primi acconti', giu2) +
                row('30 nov', 'Secondi acconti', novRate) +
                totale('Totale nell\'anno', tot2, tag2));
            setHtml('annoRegime',
                row('30 giu', 'Saldo residuo + primi acconti', giuR) +
                row('30 nov', 'Secondi acconti', novRate) +
                totale('Totale nell\'anno', annuale));
        }

        // Legenda: di cosa è fatto l'acconto (dipende dalla gestione).
        const li = (txt) => `<li>${txt}</li>`;
        const items = [li('<strong>IRPEF</strong>: acconto 100% (40% al 30/6 + 60% al 30/11).')];
        if (gestioneId === 'INPS_GS') {
            items.push(li('<strong>INPS Gestione Separata</strong>: acconto 80%, in due rate del 40% (30/6 e 30/11).'));
        } else if (gestioneId === 'INPS_ART' || gestioneId === 'INPS_COM') {
            items.push(li("<strong>INPS Artigiani/Commercianti</strong>: il minimale si versa in 4 rate fisse (senza acconto); acconto 80% solo sull'eccedenza."));
        } else if (gestioneId) {
            items.push(li('<strong>Cassa professionale / ENASARCO</strong>: acconti e scadenze secondo il regolamento della tua gestione (non stimati qui).'));
        }
        items.push(li('<strong>Addizionale comunale</strong>: acconto 30%; <strong>regionale</strong>: nessun acconto (solo saldo).'));
        items.push(li('<strong>IVA</strong>: ha un acconto a sé (entro il 27 dicembre), ma è neutra — la incassi dai clienti e la versi allo Stato, non incide sul netto.'));
        setHtml('accontoLegenda', `<ul class="piva-legenda-list">${items.join('')}</ul>`);

        // Consiglio: accantonamento annuo (imposte + contributi).
        setText('tipAccantona', euro(annuale));
    };

    // --- Confronto con il forfettario ---------------------------------------
    // L'ordinario non chiede il codice ATECO ne' l'anzianita' di attivita', che
    // servono per il forfettario: si parte da un default coerente con la forma
    // di attivita' scelta e si lascia all'utente la possibilita' di correggerlo.
    const confrontoCoefficiente = document.getElementById('confronto-coefficiente');
    const confrontoStartup = document.getElementById('confronto-startup');
    let confrontoInizializzato = false;
    // I listener dei controlli leggono sempre gli ultimi parametri, non quelli
    // catturati alla prima esecuzione: dopo una modifica rapida i valori cambiano.
    let confrontoParams = null;

    const renderConfrontoForfettario = (params) => {
        if (!document.getElementById('confrontoRegimi')) return;
        confrontoParams = params;

        if (!confrontoInizializzato) {
            if (confrontoCoefficiente) {
                confrontoCoefficiente.value = String(coefficienteDefault(params.formaAttivita));
            }
            const ricalcola = () => renderConfrontoForfettario(confrontoParams);
            confrontoCoefficiente?.addEventListener('change', ricalcola);
            confrontoStartup?.addEventListener('change', ricalcola);
            confrontoInizializzato = true;
        }

        const coefficiente = parseFloat(confrontoCoefficiente?.value) || 78;
        const aliquotaImposta = confrontoStartup?.checked ? 5 : 15;
        const calcInput = buildCalcInput(params);
        const esito = confrontaRegimi({
            ...calcInput,
            coefficienteRedditivita: coefficiente,
            aliquotaImposta,
            // La riduzione 35% esiste solo nel forfettario: la si ipotizza
            // richiesta se l'utente ha gia' dichiarato di essere un nuovo
            // iscritto Artigiani/Commercianti.
            riduzione35: Boolean(params.nuovaAttivitaArtCom) && isArtCom(params.cassa)
        }, fiscalData);

        renderConfronto(esito, {
            origine: 'ordinario',
            ctaHref: linkAlForfettario({
                // Oltre la soglia il confronto e' calcolato sul fatturato
                // massimo ammesso: il calcolatore forfettario va aperto con
                // lo stesso numero, non con ricavi che il regime non consente.
                ricavi: esito.fatturatoForfettario,
                coefficienteRedditivita: coefficiente,
                aliquotaImposta,
                formaAttivita: params.formaAttivita,
                cassa: params.cassa,
                mesiAttivita: params.mesiAttivita,
                nuovaAttivitaArtCom: params.nuovaAttivitaArtCom,
                riduzione50: params.riduzione50,
                riduzione35: Boolean(params.nuovaAttivitaArtCom) && isArtCom(params.cassa),
                dipendenteFullTime: params.dipendenteFullTime,
                ralDipendente: params.ralDipendente,
                riduzioneDipendente: params.riduzioneDipendente
            }),
            notaIpotesi: `Il coefficiente di redditività dipende dal tuo codice ATECO: qui è impostato al ${coefficiente}%, correggilo se il tuo è diverso. ` +
                'Nel forfettario non si applica l\'IVA in fattura e non si detrae quella sugli acquisti.'
        });
    };

    // Blocco "sostenuti vs dedotti": compare solo se il payload porta il
    // dettaglio del costruttore (cd) e l'invariante col totale regge ancora.
    // Payload vecchi o totale inserito a mano: pagina identica a prima.
    const renderCostiConfronto = (params) => {
        const blocco = document.getElementById('costiConfronto');
        if (!blocco) return;
        const esito = ricalcolaDettaglioCosti(params);
        if (!esito || esito.righe.length === 0 ||
            Math.round(esito.totaleDedotto) !== Math.round(params.costi || 0)) {
            blocco.style.display = 'none';
            return;
        }
        setText('costiSostenutiTot', `${formatEuro(esito.totaleSostenuto)} €`);
        setText('costiDedottiTot', `${formatEuro(esito.totaleDedotto)} €`);
        setText('costiNonDedottiTot', `${formatEuro(esito.totaleNonDedotto)} €`);
        setText('costiIvaStimata', `~${formatEuro(esito.totaleIvaStimata)} €`);
        const corpo = document.getElementById('costiConfrontoRighe');
        if (corpo) {
            corpo.innerHTML = esito.righe.map((riga) => {
                const categoria = costiCatalogo.categorie.find((c) => c.id === riga.categoriaId);
                return `<tr><td>${categoria?.label || riga.categoriaId}</td>` +
                    `<td>${formatEuro(riga.sostenuto)} €</td>` +
                    `<td>${formatEuro(riga.dedotto)} €</td></tr>`;
            }).join('');
        }
        blocco.style.display = '';
        trackConfrontoVisto();
    };

    const renderResults = (params) => {
        if (!resultSection) return;
        const r = calcolaPivaOrdinaria(buildCalcInput(params), fiscalData);

        setText('pivaNettoAnnuale', `${formatEuro(r.nettoAnnuo)} €`);
        setText('pivaNettoMensile', `${formatEuro(r.nettoMensile)} €`);
        setText('pivaHeroMensile', `≈ ${formatEuro(r.nettoMensile)} € al mese (media)`);
        setText('pivaRicaviTile', `${formatEuro(r.ricavi)} €`);

        setText('pivaBreakNetto', `${formatEuro(r.nettoAnnuo)} €`);
        setText('pivaBreakCosti', `${formatEuro(r.costi)} €`);
        // Nella ripartizione dei ricavi entra solo la quota di contributi che
        // esce davvero di tasca: l'integrativo in rivalsa e' una partita di giro.
        setText('pivaBreakContributi', `${formatEuro(r.contributiACarico)} €`);
        setText('pivaBreakIrpef', `${formatEuro(r.irpefNetta)} €`);
        setText('pivaBreakAddizionali', `${formatEuro(r.addizionaleRegionale + r.addizionaleComunale)} €`);

        document.querySelectorAll('.js-fido-tasse').forEach((el) => {
            el.textContent = `${formatEuro(r.contributiACarico + r.imposteTotali)} €`;
        });

        // Barra a 5 segmenti (percentuali dei ricavi, clampate a [0,100]).
        const pct = (v) => (r.ricavi > 0 ? Math.min(Math.max((v / r.ricavi) * 100, 0), 100) : 0);
        const setSegment = (segId, pctId, value) => {
            const share = pct(value);
            const seg = document.getElementById(segId);
            if (seg) seg.style.width = `${share}%`;
            setText(pctId, formatPct(share));
        };
        setSegment('pivaSegNetto', 'pivaPctNetto', Math.max(r.nettoAnnuo, 0));
        setSegment('pivaSegCosti', 'pivaPctCosti', r.costi);
        setSegment('pivaSegContributi', 'pivaPctContributi', r.contributiACarico);
        setSegment('pivaSegIrpef', 'pivaPctIrpef', r.irpefNetta);
        setSegment('pivaSegAddizionali', 'pivaPctAddizionali', r.addizionaleRegionale + r.addizionaleComunale);

        // Dettaglio
        setVal('ricaviDetail', `${formatEuro(r.ricavi)} €`);
        setVal('costiDetail', `${formatEuro(r.costi)} €`);
        renderCostiConfronto(params);
        setVal('redditoLordoDetail', `${formatEuro(r.redditoLordo)} €`);
        setVal('contributiDetail', `${formatEuro(r.contributi)} €`);
        // Casse professionali: l'integrativo si addebita al cliente, quindi il
        // netto sconta solo i contributi effettivamente a carico.
        const integrativoRow = document.getElementById('integrativoDetailRow');
        if (integrativoRow) {
            if (r.integrativoRivalsa > 0) {
                setVal('integrativoDetail', `${formatEuro(r.contributoIntegrativo)} €`);
                setVal('contributiACaricoDetail', `${formatEuro(r.contributiACarico)} €`);
                integrativoRow.style.display = '';
            } else {
                integrativoRow.style.display = 'none';
            }
        }
        setVal('imponibileIrpefDetail', `${formatEuro(r.imponibileIrpef)} €`);
        setVal('irpefLordaDetail', `${formatEuro(r.irpefLorda)} €`);
        setVal('detrazioneDetail', `${formatEuro(r.detrazioni)} €`);
        setVal('irpefNettaDetail', `${formatEuro(r.irpefNetta)} €`);

        // Con reddito da dipendente la riga non è più una detrazione che spetta,
        // ma la variazione (in genere una perdita) delle detrazioni già godute.
        if (r.redditoDipendente > 0 && r.detrazioni < 0) {
            setText('detrazioneDetailLabel', 'Variazione detrazioni');
            setText('detrazioneDetailTipTitle', 'Variazione delle detrazioni');
            setText('detrazioneDetailTipText',
                'Il reddito P.IVA si somma allo stipendio e fa scendere le detrazioni da lavoro dipendente ' +
                'già godute in busta paga (e l\'ulteriore detrazione tra 20.000 e 40.000 €). La differenza ' +
                'torna a debito in dichiarazione ed è un costo reale della partita IVA. ' +
                'La detrazione da lavoro autonomo non si somma a quella da dipendente: si sceglie la più favorevole (art. 13 c. 5 TUIR).');
        } else {
            setText('detrazioneDetailLabel', 'Detrazione lavoro autonomo');
            setText('detrazioneDetailTipTitle', 'Detrazione lavoro autonomo');
            setText('detrazioneDetailTipText',
                'Detrazione IRPEF spettante sui redditi di lavoro autonomo (art. 13 TUIR): riduce l\'imposta lorda. ' +
                'Decresce all\'aumentare del reddito e si azzera oltre 50.000 €.');
        }
        setVal('addizionaleRegionaleDetail', `${formatEuro(r.addizionaleRegionale)} €`);
        setVal('addizionaleComunaleDetail', `${formatEuro(r.addizionaleComunale)} €`);
        setVal('imposteTotaliDetail', `${formatEuro(r.imposteTotali)} €`);
        setVal('nettoAnnuoDetail', `${formatEuro(r.nettoAnnuo)} €`);
        setVal('nettoMensileDetail', `${formatEuro(r.nettoMensile)} €`);

        // Nota: con reddito da dipendente/pensione l'IRPEF della P.IVA è calcolata
        // all'aliquota marginale sul reddito complessivo.
        const marginaleNota = document.getElementById('marginaleNota');
        if (marginaleNota) {
            if (r.redditoDipendente > 0) {
                let testo =
                    `IRPEF e addizionali calcolate all'<strong>aliquota marginale</strong> ` +
                    `sul reddito complessivo (dipendente ${formatEuro(r.redditoDipendente)} € + P.IVA ${formatEuro(r.imponibileIrpef)} € ` +
                    `= ${formatEuro(r.imponibileTotale)} €), perché nel regime ordinario i redditi si sommano.`;
                if (r.detrazioni < 0) {
                    testo += ` Il conto include <strong>${formatEuro(-r.detrazioni)} €</strong> di detrazioni che perdi ` +
                        `sullo stipendio: si ricalcolano sul reddito complessivo e tornano a debito in dichiarazione.`;
                }
                if (r.sommaIntegrativaPersa > 0) {
                    testo += ` ⚠️ Superando i 20.000 € di reddito complessivo perdi anche il trattamento integrativo, ` +
                        `circa <strong>${formatEuro(r.sommaIntegrativaPersa)} €</strong> l'anno in busta paga: non è un'imposta, ` +
                        `quindi non è conteggiato qui sopra, ma è un costo in più della partita IVA.`;
                }
                marginaleNota.innerHTML = `<i class="ph ph-info"></i><span>${testo}</span>`;
                marginaleNota.style.display = 'flex';
            } else {
                marginaleNota.style.display = 'none';
            }
        }

        // Massimale contributivo: oltre la soglia il reddito non è più assoggettato.
        const massimaleNota = document.getElementById('massimaleNota');
        if (massimaleNota) {
            if (r.massimaleApplicato) {
                massimaleNota.innerHTML =
                    `<i class="ph ph-info"></i><span>Contributi calcolati fino al <strong>massimale annuo di ` +
                    `${formatEuro(r.massimaleContributivo)} €</strong>: sulla parte di reddito eccedente ` +
                    `(${formatEuro(r.imponibileContributi - r.imponibilePrevidenziale)} €) non si versano contributi.</span>`;
                massimaleNota.style.display = 'flex';
            } else {
                massimaleNota.style.display = 'none';
            }
        }

        // Casse professionali modellate a percentuale pura: avvisa che i minimi
        // della cassa non sono compresi nella stima.
        const minimiCassaNota = document.getElementById('minimiCassaNota');
        if (minimiCassaNota) {
            const gestione = findGestione(params.cassa);
            if (gestione?.minimi_non_modellati) {
                minimiCassaNota.innerHTML =
                    `<i class="ph ph-warning"></i><span>Per <strong>${gestione.label}</strong> la stima applica solo ` +
                    `l'aliquota sul reddito: <strong>non include i contributi minimi</strong> (soggettivo e integrativo) ` +
                    `né il contributo di maternità previsti dalla cassa, che sono dovuti anche a reddito basso. ` +
                    `Verifica gli importi sul sito della tua cassa.</span>`;
                minimiCassaNota.style.display = 'flex';
            } else {
                minimiCassaNota.style.display = 'none';
            }
        }

        renderAcconti(r, params.cassa);
        renderConfrontoForfettario(params);

        if (inputSection) inputSection.style.display = 'none';
        resultSection.style.display = 'block';
        window.scrollTo(0, 0);
    };

    // --- Quick edit (ricavi + costi) ----------------------------------------
    const quickModal = document.getElementById('pivaQuickEditModal');
    const quickClose = document.getElementById('pivaQuickEditClose');
    const quickForm = document.getElementById('pivaQuickEditForm');
    const quickRicavi = document.getElementById('piva-quick-ricavi');
    const quickRicaviFormatted = document.getElementById('piva-quick-ricavi-formatted');
    const quickCosti = document.getElementById('piva-quick-costi');
    const quickToConfig = document.getElementById('pivaQuickEditToConfigurator');
    let quickScrollY = 0;

    const showQuickFormatted = () => {
        if (!quickRicavi || !quickRicaviFormatted) return;
        let v = parseFloat(quickRicavi.value) || 0;
        if (v < 0) v = 0;
        quickRicavi.value = Math.round(v);
        if (v > 0) {
            quickRicaviFormatted.value = `${Math.round(v).toLocaleString('it-IT')} €`;
            quickRicavi.style.display = 'none';
            quickRicaviFormatted.style.display = 'block';
        } else {
            quickRicaviFormatted.value = '';
            quickRicavi.style.display = 'block';
            quickRicaviFormatted.style.display = 'none';
        }
    };

    const openQuickModal = () => {
        if (!isResultsPage || !quickModal) return;
        const params = getParamsFromUrl();
        if (quickRicavi) quickRicavi.value = Math.round(params?.ricavi || 0);
        if (quickCosti) quickCosti.value = Math.round(params?.costi || 0);
        showQuickFormatted();
        quickModal.classList.add('is-open');
        quickModal.setAttribute('aria-hidden', 'false');
        quickScrollY = window.scrollY || 0;
        document.documentElement.classList.add('modal-open');
        document.body.classList.add('modal-open');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${quickScrollY}px`;
        document.body.style.width = '100%';
    };

    const closeQuickModal = () => {
        if (!quickModal) return;
        quickModal.classList.remove('is-open');
        quickModal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('modal-open');
        document.body.classList.remove('modal-open');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, quickScrollY);
    };

    // ========================================================================
    // Inizializzazione per pagina
    // ========================================================================
    if (!isResultsPage) {
        const params = getParamsFromUrl();
        if (params) applyParamsToForm(params);
        // initComuneAutocomplete DOPO aver valorizzato regione/comune, cosi'
        // ricompone l'eventuale selezione ufficiale al ritorno dai risultati.
        initComuneAutocomplete();

        // Costruttore di costi deducibili: legge il contesto (ricavi, forma,
        // cassa) sempre fresco dal form e scrive il totale dedotto in #costi.
        initCostiBuilder({
            costiElement,
            getContesto: () => ({
                ricavi: parseFloat(ricaviElement?.value) || 0,
                formaAttivita: formaAttivitaElement?.value || '',
                cassaId: cassaElement?.value || ''
            })
        });
        if (params?.cd) applicaDettaglio(params.cd, params.costi);

        requiredFields.forEach((id) => {
            const el = document.getElementById(id);
            if (el) {
                el.addEventListener('input', updateToggleState);
                el.addEventListener('change', updateToggleState);
            }
        });
        formaAttivitaElement?.addEventListener('change', () => {
            updateCassaOptions();
            updateArtComVisibility();
            updateMandantiVisibility();
            updateFasciaEtaVisibility();
            updateContabilitaVisibility();
            updateToggleState();
            aggiornaContestoCosti();
        });
        cassaElement?.addEventListener('change', () => {
            updateArtComVisibility();
            updateMandantiVisibility();
            updateFasciaEtaVisibility();
            updateToggleState();
            aggiornaContestoCosti();
        });
        // I plafond del costruttore (2% vitto, 1%/1,5% rappresentanza) si
        // muovono coi ricavi: ricalcola il dettaglio a ogni modifica.
        ricaviElement?.addEventListener('input', aggiornaContestoCosti);
        dipendenteFullTimeElement?.addEventListener('change', () => {
            updateDipendenteVisibility();
            updateArtComVisibility();
        });
        riduzioneDipendenteElement?.addEventListener('change', updateArtComVisibility);
        [nuovaAttivitaArtComElement, riduzione50Element, mesiAttivitaElement,
            ante1996Element, numeroMandantiElement, fasciaEtaEnpamElement,
            contabilitaOrdinariaElement].forEach((el) =>
            el?.addEventListener('change', updateToggleState));

        setupRicaviMirror();
        if (cassaElement) cassaElement.disabled = !formaAttivitaElement?.value;
        updateCassaOptions();
        updateArtComVisibility();
        updateMandantiVisibility();
        updateFasciaEtaVisibility();
        updateContabilitaVisibility();
        updateDipendenteVisibility();
        updateToggleState();

        toggleButton?.addEventListener('click', () => redirectToResults());
    } else {
        const params = getParamsFromUrl();
        if (!params || !params.ricavi || !params.cassa || !params.regione) {
            redirectToHomeWithQuery();
            return;
        }
        renderResults(params);

        backButton?.addEventListener('click', () => redirectToHomeWithQuery());
        backButtonTop?.addEventListener('click', () => openQuickModal());
        shareButton?.addEventListener('click', () => handleShareClick(shareButton, { campaign: SHARE_CAMPAIGN }));

        const scrollTo = (sel) => document.querySelector(sel)?.scrollIntoView({ behavior: 'smooth' });
        detailScrollButton?.addEventListener('click', () => scrollTo('#dettaglio-calcolo'));
        faqScrollButton?.addEventListener('click', () => scrollTo('.faq-container'));
        accontiScrollButton?.addEventListener('click', () => scrollTo('#acconti-panel'));

        quickClose?.addEventListener('click', closeQuickModal);
        quickModal?.addEventListener('click', (e) => { if (e.target === quickModal) closeQuickModal(); });
        quickRicavi?.addEventListener('blur', showQuickFormatted);
        quickRicaviFormatted?.addEventListener('focus', () => {
            quickRicaviFormatted.style.display = 'none';
            quickRicavi.style.display = 'block';
            quickRicavi.focus();
        });
        quickToConfig?.addEventListener('click', () => {
            const qs = window.location.search;
            window.location.href = qs ? `${BASE_PATH}${qs}` : BASE_PATH;
        });
        quickForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            let nextRicavi = parseFloat(quickRicavi?.value) || 0;
            if (nextRicavi < 0) nextRicavi = 0;
            let nextCosti = parseFloat(quickCosti?.value) || 0;
            if (nextCosti < 0) nextCosti = 0;
            if (!nextRicavi) return;
            // Base: l'ultimo payload in URL (un submit precedente puo' gia'
            // aver tolto il dettaglio cd; params in closure sarebbe stantio).
            const updated = { ...(getParamsFromUrl() || params), ricavi: Math.round(nextRicavi), costi: Math.round(nextCosti) };
            // Invariante cd => costi: il dettaglio del costruttore resta nel
            // payload solo se il totale coincide ancora. Cambiando i costi a
            // mano si stacca; cambiando i ricavi i plafond possono spostarlo.
            if (updated.cd) {
                const esito = ricalcolaDettaglioCosti(updated);
                if (!esito || Math.round(esito.totaleDedotto) !== updated.costi) delete updated.cd;
            }
            const encoded = encodeParams(updated);
            const qs = encoded ? `d=${encodeURIComponent(encoded)}` : '';
            window.history.replaceState({}, '', qs ? `${RESULTS_PATH}?${qs}` : RESULTS_PATH);
            renderResults(updated);
            closeQuickModal();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && quickModal?.classList.contains('is-open')) closeQuickModal();
        });
    }
});
